import * as React from "react";
import { useState } from "react";
import { WorkflowNode } from "../../types";
import { Divider, FormGroup, ToggleGroup } from "../RightAsideHelpers";
import Input from "@/components/Input";
import TextArea from "@/components/TextArea";
import Select from "@/components/Select";
import VariablePicker from "@/components/VariablePicker";
import { useMemo } from "react";
import useSubscriptions from "@/hooks/useSubscriptions";
import StarterWorkflowWizard from "@/pages/Messaging/Integrations/StarterWorkflowWizard";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const QUICK_STARTS = [
  {
    label: "Support follow-up",
    instructions: "Write a warm follow-up email summarising the support issue that was just resolved. Include what was done to resolve it, any next steps the customer should take, and how to reach us if they need further help.",
    tone: "friendly",
    includeTranscript: true
  },
  {
    label: "Quote delivery",
    instructions: "Write a professional email thanking the customer for their interest, providing a brief summary of what we discussed, and letting them know a detailed proposal will follow within one business day.",
    tone: "professional",
    includeTranscript: true
  },
  {
    label: "Appointment confirmation",
    instructions: "Write a friendly appointment confirmation email with the booking details, what the customer should prepare or bring, the cancellation/rescheduling policy (24 hours notice), and a contact method for questions.",
    tone: "friendly",
    includeTranscript: true
  },
  {
    label: "Escalation notice",
    instructions: "Write a professional email notifying the customer that their case has been escalated to a senior team member, providing the case number, estimated response time, and a reassurance that we are prioritising their issue.",
    tone: "professional",
    includeTranscript: false
  }
];

export default function AiComposeEmailEditor({ node, onUpdate }: Props) {
  const { subscription } = useSubscriptions();
  const [showWizard, setShowWizard] = useState(false);

  const toneOptions = useMemo(() => ({
    professional: "Professional",
    friendly: "Friendly",
    formal: "Formal",
    urgent: "Urgent"
  }), []);

  if (!subscription?.enableAiComposedEmails) {
    return (
      <div className="space-y-3">
        <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-900">
          <div className="font-semibold mb-1">AI Compose Email</div>
          <p className="text-violet-700 mb-2">
            Describe what you need — Claude writes a personalised email from conversation context and sends it automatically. Included in <strong>Professional+</strong> or available as a $149/mo add-on.
          </p>
          <p className="text-violet-600 text-xs">Contact your account manager to enable this feature.</p>
        </div>
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500 space-y-1">
          <div className="font-medium text-slate-600">What you can do with it:</div>
          <div>— Post-support follow-up emails, auto-written from the conversation</div>
          <div>— Quote delivery emails with context from what was discussed</div>
          <div>— Appointment confirmations with personalised details</div>
          <div>— Any email, described in plain language</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showWizard && (
        <StarterWorkflowWizard integration="ai-email" onClose={() => setShowWizard(false)} />
      )}

      {/* Intro banner */}
      <div className="mb-3 p-3 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-800 flex items-start gap-2">
        <div className="flex-1">
          <span className="font-medium">AI Compose Email</span> — describe what you need and Claude writes a polished, personalised email from conversation context.
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="text-[11px] whitespace-nowrap font-medium text-violet-700 underline underline-offset-2 hover:text-violet-900 flex-shrink-0"
        >
          Use a template
        </button>
      </div>

      {/* Quick-start chips */}
      {!node.aiEmailInstructions && (
        <div className="mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Quick start</div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_STARTS.map((q) => (
              <button
                key={q.label}
                onClick={() => onUpdate({
                  aiEmailInstructions: q.instructions,
                  aiEmailTone: q.tone,
                  aiEmailIncludeTranscript: q.includeTranscript
                })}
                className="text-[11px] px-2 py-1 rounded-full border border-violet-200 bg-white text-violet-700 hover:bg-violet-50 transition-colors"
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <FormGroup
        label="Instructions"
        tooltipText="Describe what the email should say in plain language. Claude will write a polished email based on your instructions and conversation context."
      >
        <TextArea
          value={node.aiEmailInstructions || ""}
          onChange={(e) => onUpdate({ aiEmailInstructions: e.currentTarget.value })}
          placeholder={"e.g. \"Summarise the support case and let the client know we're following up within 24 hours.\""}
          rows={4}
        />
      </FormGroup>

      <FormGroup
        label="Recipient Email"
        tooltipText="Where to send the email. Leave blank to use the contact's email on file. Supports <<variable>> substitution."
      >
        <Input
          type="text"
          value={node.aiEmailTo || ""}
          onChange={(aiEmailTo) => onUpdate({ aiEmailTo })}
          placeholder="Leave blank to use contact's email on file"
          suffixIcon={
            <VariablePicker
              onInsert={(v) => onUpdate({ aiEmailTo: (node.aiEmailTo || "") + v })}
            />
          }
        />
      </FormGroup>

      <FormGroup
        label="Subject (optional)"
        tooltipText="If provided, the AI uses this exact subject line. Leave blank to let the AI generate one from your instructions."
      >
        <Input
          type="text"
          value={node.aiEmailSubject || ""}
          onChange={(aiEmailSubject) => onUpdate({ aiEmailSubject })}
          placeholder="Leave blank — Claude will generate a subject"
          suffixIcon={
            <VariablePicker
              onInsert={(v) => onUpdate({ aiEmailSubject: (node.aiEmailSubject || "") + v })}
            />
          }
        />
      </FormGroup>

      <Divider />

      <FormGroup
        label="Tone"
        tooltipText="The tone Claude should use when writing the email."
      >
        <Select
          options={toneOptions}
          value={node.aiEmailTone || "professional"}
          onChange={(aiEmailTone) => onUpdate({ aiEmailTone })}
        />
      </FormGroup>

      <ToggleGroup
        toggled={node.aiEmailIncludeTranscript || false}
        label="Include Conversation Context"
        helpText="Pass recent conversation messages to Claude so it can personalise the email with specific details discussed."
        changeToggle={() => onUpdate({ aiEmailIncludeTranscript: !node.aiEmailIncludeTranscript })}
      />
    </>
  );
}
